/**
 * me/badges.tsx — Standalone badge grid surface (N-06 polish).
 *
 * Lives at `/me/badges`. Reads from `/api/me/badges` which lazily
 * evaluates the user's current progress against badge rules before
 * returning. Earned / attainable-soon / locked sections are rendered
 * with the existing `<BadgeGrid>` component (extended for new layout).
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { StatePanel } from '../../components/ui/StatePanel.js';
import { BadgeGrid } from '../../components/student/BadgeGrid.js';
import { BadgeChip } from '../../components/student/BadgeChip.js';
import {
  freshSessionQuery,
  myBadgesQuery,
  type BadgeEntry,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/me/badges')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    return null;
  },
  component: BadgesPage,
});

function BadgesPage(): ReactNode {
  const badges = useQuery(myBadgesQuery());
  const earned: BadgeEntry[] = badges.data?.ok
    ? badges.data.data.earned
    : [];
  const soon: BadgeEntry[] = badges.data?.ok
    ? badges.data.data.attainableSoon
    : [];
  const locked: BadgeEntry[] = badges.data?.ok ? badges.data.data.locked : [];
  const allBadges = [...earned, ...soon, ...locked];

  return (
    <main className="page-shell" data-testid="badges-page">
      <PageHeader
        eyebrow="Lencana"
        title="Lencana kamu"
        description="Lencana adalah pencapaian yang kamu kumpulkan selama belajar — lihat apa saja yang sudah kamu raih, apa yang hampir tercapai, dan apa yang masih menunggu."
      />

      <section className="section" aria-label="Lencana yang sudah diperoleh">
        <h2 className="section-title">Sudah diperoleh</h2>
        {badges.isLoading ? (
          <p className="form-message">Memuat lencana…</p>
        ) : allBadges.length === 0 ? (
          <StatePanel
            variant="empty"
            title="Belum ada lencana"
            description="Selesaikan satu level atau worksheet untuk mulai mendapatkan lencana. Lencana muncul otomatis saat kamu mencapai tonggak."
          />
        ) : (
          <BadgeGrid
            badges={allBadges.map((b) => ({
              id: b.badgeId,
              title: b.title,
              description: b.description,
              earnedAt: b.awardedAt,
              icon: '', // resolved by BadgeChip; BadgeGrid fallback
              accent: 'mint',
            }))}
          />
        )}
      </section>

      {soon.length > 0 ? (
        <section
          className="section"
          aria-label="Lencana yang hampir tercapai"
          data-testid="badges-section-attainable-soon"
        >
          <h2 className="section-title">Hampir tercapai</h2>
          <p className="form-message">
            Lencana-lencana ini sedang kamu gapai — sedikit lagi sampai bisa diklaim.
          </p>
          <div className="cluster" data-testid="badges-attempt-list">
            {soon.map((b) => (
              <BadgeChip key={b.badgeId} badge={b} state="attainableSoon" />
            ))}
          </div>
        </section>
      ) : null}

      <p className="form-message" data-testid="badges-count-summary">
        Total {earned.length} lencana diperoleh
        {soon.length > 0 ? `, ${soon.length} hampir tercapai` : ''}.
        {' '}Lencana baru otomatis muncul saat kamu mencapai syarat.
      </p>

      <p>
        <Link to="/me" className="button button-secondary">
          ← Kembali ke dasbor siswa
        </Link>
      </p>
    </main>
  );
}
